import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { routeForNotification } from "@/lib/notificationRoute";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Cake, Key, ShieldCheck, Megaphone, CheckCircle, CurrencyCircleDollar } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Sağ üstteki bildirim zili. Unread sayısını badge olarak gösterir; tıklanınca
 * son 8 bildirimi hızlı bir popover'da listeler. 60 sn'de bir arka planda
 * yeniler. Herhangi bir bildirime tıklamak onu "okundu" yapar.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch { /* sessizce yut */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read);
  const preview = items.slice(0, 8);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch {}
  };

  const onItemClick = (n) => {
    if (!n.read) markRead(n.id);
    const route = routeForNotification(n);
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      toast.success("Tüm bildirimler okundu olarak işaretlendi");
      load();
    } catch { toast.error("İşlem başarısız"); }
  };

  const iconFor = (n) => {
    if (n.type === "birthday") return <Cake size={16} weight="fill" className="text-pink-500" />;
    if (n.type === "hr_password_change_request" || n.type === "password_reset_request") return <Key size={16} className="text-amber-500" />;
    if (n.type === "device_approval_request") return <ShieldCheck size={16} className="text-emerald-500" />;
    if (n.type === "announcement") return <Megaphone size={16} className="text-blue-500" />;
    if (n.type === "leave_payment") return <CurrencyCircleDollar size={16} weight="fill" className="text-emerald-600" />;
    return <Bell size={16} className="text-muted-foreground" />;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="topbar-bell" className="relative">
          <Bell size={18} />
          {unread.length > 0 && (
            <span
              data-testid="bell-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
            >
              {unread.length > 99 ? "99+" : unread.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="bell-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="font-display font-semibold text-sm">Bildirimler</div>
          {unread.length > 0 && (
            <button onClick={markAllRead} className="text-[11px] text-primary hover:underline flex items-center gap-0.5" data-testid="bell-mark-all-read">
              <CheckCircle size={12} /> Tümünü okundu yap
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {preview.length === 0 && (
            <div className="p-6 text-center text-xs text-muted-foreground">Henüz bildirim yok</div>
          )}
          {preview.map((n) => (
            <div
              key={n.id}
              className={`px-3 py-2 border-b border-border/60 cursor-pointer hover:bg-muted/40 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
              onClick={() => onItemClick(n)}
              role="button"
              data-testid={`bell-item-${n.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{iconFor(n)}</div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs leading-snug ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.message}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(n.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5" />}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-border p-2 text-center">
          <Link to="/notifications" className="text-xs text-primary hover:underline" onClick={() => setOpen(false)} data-testid="bell-see-all">
            Tüm bildirimleri gör →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
